<script setup lang="ts">
/**
 * Reusable narrative block editor — the Editor.js integration extracted from
 * the original POC page (NarrativeEditorPage.vue) so it can be embedded inside
 * the Narratives workspace shell and reused by the standalone POC.
 *
 * Owns the Editor.js instance, the registered tools (header/list/quote/marker/
 * inline-code plus the bespoke finding-link + remove-link tools), and the
 * finding-picker dialog the inline tool drives. The host passes seed content
 * and the available findings, and calls `save()` (exposed) to read the current
 * OutputData back out. No persistence here — the host owns saving.
 */
import { onBeforeUnmount, onMounted, ref, shallowRef, computed } from 'vue';
import EditorJS, { type OutputData } from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Quote from '@editorjs/quote';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';
import { FindingLinkTool, type Finding, type FindingLinkConfig } from './findingLinkTool';
import { RemoveLinkTool } from './removeLinkTool';

const props = withDefaults(
    defineProps<{
        initialData?: OutputData;
        findings?: Finding[];
        placeholder?: string;
        autofocus?: boolean;
    }>(),
    {
        findings: () => [],
        placeholder: 'Start writing the narrative…',
        autofocus: true,
    },
);

const emit = defineEmits<{
    // A finding link in the content was clicked — the host reveals the finding
    // (e.g. opens the findings panel). Carries the finding id.
    (e: 'finding-click', findingId: string): void;
}>();

const holder = ref<HTMLDivElement | null>(null);
const editor = shallowRef<EditorJS | null>(null);

// Finding links use a custom (non-navigable) href scheme, so a plain click
// would do nothing useful. Intercept clicks on `.finding-link` anchors and let
// the host reveal the finding instead of the browser navigating.
function onHolderClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest?.('a.finding-link') as HTMLElement | null;
    if (!anchor) return;
    const findingId = anchor.dataset.findingId;
    // Don't hijack link-editing clicks (e.g. ctrl/meta held); only plain clicks.
    if (!findingId || event.metaKey || event.ctrlKey || event.altKey) return;
    event.preventDefault();
    emit('finding-click', findingId);
}

// ── Finding picker dialog (promise-based bridge to the inline tool) ───────
const pickerOpen = ref(false);
const pickerQuery = ref('');
let resolvePicker: ((finding: Finding | null) => void) | null = null;

const filteredFindings = computed(() => {
    const q = pickerQuery.value.trim().toLowerCase();
    if (!q) return props.findings;
    return props.findings.filter(
        (f) => f.label.toLowerCase().includes(q) || f.code.toLowerCase().includes(q),
    );
});

// Handed to the inline tool; it awaits this when the user clicks the button.
function requestFinding(): Promise<Finding | null> {
    pickerQuery.value = '';
    pickerOpen.value = true;
    return new Promise((resolve) => {
        resolvePicker = resolve;
    });
}

function chooseFinding(finding: Finding) {
    pickerOpen.value = false;
    resolvePicker?.(finding);
    resolvePicker = null;
}

function cancelPicker() {
    pickerOpen.value = false;
    resolvePicker?.(null);
    resolvePicker = null;
}

onMounted(() => {
    if (!holder.value) return;
    editor.value = new EditorJS({
        holder: holder.value,
        autofocus: props.autofocus,
        placeholder: props.placeholder,
        data: props.initialData,
        tools: {
            header: {
                class: Header as never,
                inlineToolbar: true,
                config: {
                    placeholder: 'Heading',
                    levels: [1, 2, 3],
                    defaultLevel: 2,
                },
            },
            list: {
                class: List as never,
                inlineToolbar: true,
            },
            quote: {
                class: Quote as never,
                inlineToolbar: true,
            },
            marker: { class: Marker as never },
            inlineCode: { class: InlineCode as never },
            findingLink: {
                class: FindingLinkTool as never,
                config: { requestFinding } satisfies FindingLinkConfig,
            },
            // One button that clears any link under the caret — generic
            // links (built-in `link` tool) and finding links alike.
            removeLink: { class: RemoveLinkTool as never },
        },
    });
});

onBeforeUnmount(() => {
    editor.value?.destroy?.();
    editor.value = null;
});

// Read the current document back out. Returns null until the editor mounts.
async function save(): Promise<OutputData | null> {
    if (!editor.value) return null;
    return editor.value.save();
}

// Replace the editor content (used by the POC's Reset).
async function render(data: OutputData): Promise<void> {
    await editor.value?.render(data);
}

defineExpose({ save, render });
</script>

<template>
    <div class="narrative-editor">
        <div ref="holder" class="narrative-editor-holder" @click="onHolderClick"></div>

        <!-- Finding picker dialog, opened by the inline tool's requestFinding(). -->
        <div v-if="pickerOpen" class="finding-picker-overlay" @click.self="cancelPicker">
            <div class="finding-picker" role="dialog" aria-label="Select a finding">
                <header class="finding-picker-head">
                    <h2 class="finding-picker-title">Link to a finding</h2>
                    <button
                        type="button"
                        class="finding-picker-close"
                        aria-label="Cancel"
                        @click="cancelPicker"
                    >
                        <span class="material-icons">close</span>
                    </button>
                </header>
                <input
                    v-model="pickerQuery"
                    type="text"
                    class="finding-picker-search"
                    placeholder="Search findings…"
                    autofocus
                />
                <ul class="finding-picker-list">
                    <li v-for="f in filteredFindings" :key="f.id">
                        <button
                            type="button"
                            class="finding-picker-item"
                            @click="chooseFinding(f)"
                        >
                            <span class="finding-picker-code">{{ f.code }}</span>
                            <span class="finding-picker-label">{{ f.label }}</span>
                        </button>
                    </li>
                    <li v-if="filteredFindings.length === 0" class="finding-picker-empty">
                        No findings match “{{ pickerQuery }}”.
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<style scoped>
.narrative-editor {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.narrative-editor-holder {
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: 8px 16px;
    min-height: 320px;
    background: var(--color-bg-white);
}

/* The finding links rendered inside the Editor.js content. :deep() because
   the anchors are created imperatively by the inline tool, outside Vue's
   scoped-style rewriting. */
.narrative-editor-holder :deep(.finding-link) {
    color: var(--color-secondary, #2563eb);
    background: rgba(37, 99, 235, 0.1);
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
    border-radius: 3px;
    padding: 0 2px;
    cursor: pointer;
}

/* ── Finding picker dialog ─────────────────────────────────────────────── */
.finding-picker-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: var(--z-modal, 1000);
}

.finding-picker {
    width: 100%;
    max-width: 460px;
    background: var(--color-bg-white);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg, 0 10px 40px rgba(0, 0, 0, 0.25));
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.finding-picker-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.finding-picker-title {
    margin: 0;
    font-size: var(--text-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.finding-picker-close {
    border: none;
    background: none;
    cursor: pointer;
    color: var(--color-text-secondary);
    display: inline-flex;
}

.finding-picker-close .material-icons {
    font-size: 20px;
}

.finding-picker-search {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    box-sizing: border-box;
}

.finding-picker-list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 280px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.finding-picker-item {
    width: 100%;
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: var(--text-sm);
    color: var(--color-text-primary);
}

.finding-picker-item:hover {
    background: var(--color-bg-subtle, #f4f4f5);
    border-color: var(--color-border);
}

.finding-picker-code {
    flex-shrink: 0;
    font-weight: var(--font-weight-semibold);
    color: var(--color-secondary, #2563eb);
    font-variant-numeric: tabular-nums;
}

.finding-picker-empty {
    padding: 10px;
    color: var(--color-text-secondary);
    font-size: var(--text-sm);
}
</style>
